import { useState, useRef, useEffect } from "react";
import { Button, Form, message, notification } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import "./style.css";
import InputCard from "../../../../Components/Cards/InputCard";
import FuelType from "../../../../Components/Cards/FuelTypeCard";
import GenericOperatingHours from "../../../../Components/Tables/GenericOperatingHourTables";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import LocationMap from "../../../../Components/Map/LocationPicker";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getVendorById,
  updateVendorDetails,
  getFuelTypes,
  deleteVendorDocument,
} from "../../../../API/Admin/FuelVendors";
import {
  getVendorTypes,
  getFuelBrands,
  getDocumentCategories,
} from "../../../../API/Config";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const MAX_FILENAME_LENGTH = 60;

const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
  if (file.name.length <= maxLength) return file;

  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
  const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
  const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

  return new File([file], `${truncatedBase}${ext}`, { type: file.type });
};

const EditVendorDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [fuelTypeCatalog, setFuelTypeCatalog] = useState([]);
  const [vendorTypeOptions, setVendorTypeOptions] = useState([]);
  const [fuelBrandOptions, setFuelBrandOptions] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const watchedLatitude = Form.useWatch("latitude", form);
  const watchedLongitude = Form.useWatch("longitude", form);

  useEffect(() => {
    const fetchFuelTypes = async () => {
      try {
        const res = await getFuelTypes();
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setFuelTypeCatalog(list);
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel types."),
        );
      }
    };

    const fetchVendorTypes = async () => {
      try {
        const res = await getVendorTypes();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setVendorTypeOptions(
            list.map((type) =>
              typeof type === "string"
                ? type
                : { label: type.name, value: type.name },
            ),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load vendor types."),
        );
      }
    };

    const fetchFuelBrands = async () => {
      try {
        const res = await getFuelBrands();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setFuelBrandOptions(
            list.map((brand) =>
              typeof brand === "string"
                ? { label: brand, value: brand }
                : { label: brand.name, value: brand.id ?? brand.name },
            ),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel brands."),
        );
      }
    };

    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fuel-vendor");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchFuelTypes();
    fetchVendorTypes();
    fetchFuelBrands();
    fetchDocumentCategories();
  }, []);

  const [operatingHours, setOperatingHours] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No vendor selected. Please open this page from the Vendor Detail page.",
      );
      return;
    }

    const fetchVendor = async () => {
      try {
        const res = await getVendorById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          form.setFieldsValue({
            legalEntityName: data.legal_name || "",
            brandName: data.brand_name || "",
            vendorType: data.vendor_type || "",
            currency: data.currency || "JMD",
            contactPhone: data.contact_phone || "",
            registrationContactPhone: data.registration_contact_phone || "",
            headquartersaddress: data.address || "",
            primarycontactperson: data.primary_contact_person || "",
            businessemail: data.contact_email || "",
            stationName: data.station_name || "",
            stationId: data.station_id || "",
            fullPhysicalAddress: data.station_address || "",
            fuelBrand: data.fuel_brand?.id ?? data.fuel_brand ?? undefined,
            latitude: data.latitude ?? "",
            longitude: data.longitude ?? "",
            bankName: data.bank_details?.bank_name || "",
            bankAddress: data.bank_details?.bank_address || "",
            accountHolderName: data.bank_details?.account_holder_name || "",
            accountnumber: data.bank_details?.account_number || "",
            accountType: data.bank_details?.account_type || "",
            swiftBicCode: data.bank_details?.swift_code || "",
            wipayAccountNumber: data.wipay_account_number || "",
            wipayApiKey: data.wipay_api_key || "",
          });
          setAvatar(resolveFileUrl(data.avatar));
          setFuelTypes(
            Array.isArray(data.fuel_types_offered)
              ? data.fuel_types_offered.map((fuel) => fuel.name)
              : [],
          );
          setOperatingHours(
            Array.isArray(data.operating_hours) ? data.operating_hours : [],
          );
          setDocuments(
            Array.isArray(data.documents)
              ? data.documents.map((doc) => ({
                  id: doc.id,
                  key: String(doc.id),
                  name:
                    doc.document_name ||
                    doc.file?.split("/").pop() ||
                    doc.category ||
                    "-",
                  category: doc.category || "-",
                  allowedFormats: doc.allowed_formats || [],
                  date: formatDate(doc.uploaded_at),
                  expiryDate: doc.expiry_date || null,
                  status: "Pending",
                  fileUrl: resolveFileUrl(doc.file),
                }))
              : [],
          );
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load vendor."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load vendor."),
        );
      }
    };

    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedId]);

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const shortened = shortenFileName(file);
      setAvatarFile(shortened);

      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(shortened);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // FIRST INPUT CARD FIELDS
  const vendorFields = [
    {
      name: "legalEntityName",
      label: "Legal Entity Name",
      placeholder: "Enter Legal Entity Name",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Legal entity name is required" }],
    },
    {
      name: "brandName",
      label: "Brand Name",
      placeholder: "Enter Brand Name",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Brand name is required" }],
    },
    {
      name: "currency",
      label: "Currency",
      placeholder: "Select Currency",
      fullWidth: false,
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "vendorType",
      label: "Vendor Type",
      placeholder: "Select Vendor Type",
      fullWidth: false,
      type: "select",
      options: vendorTypeOptions,
    },
  ];

  // SECOND INPUT CARD FIELDS
  const contactInfoFields = [
    {
      name: "headquartersaddress",
      label: "Headquarters Address",
      placeholder: "Enter Address of Headquarters",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Address is required" }],
    },
    {
      name: "primarycontactperson",
      label: "Primary Contact Person",
      placeholder: "Enter Contact Of Primary Person",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "businessemail",
      label: "Business Email",
      placeholder: "Enter Email Of Primary Person",
      fullWidth: false,
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "contactPhone",
      label: "Phone Number",
      placeholder: "Enter Phone Number",
      fullWidth: false,
      type: "phone",
      rules: [{ required: true, message: "Contact number is required" }],
    },
    {
      name: "registrationContactPhone",
      label: "Registration Contact Phone",
      placeholder: "Enter Registration Contact Phone",
      fullWidth: false,
      type: "phone",
    },
  ];

  const bankingDetailsFields = [
    {
      name: "bankName",
      label: "Bank Name",
      placeholder: "Enter Bank Name",
      fullWidth: false,
      type: "text",
    },
    {
      name: "bankAddress",
      label: "Bank Address",
      placeholder: "Enter Bank Address",
      fullWidth: false,
      type: "text",
    },
    {
      name: "accountHolderName",
      label: "Account Holder Name",
      placeholder: "Enter Account Holder Name",
      fullWidth: false,
      type: "text",
    },
    {
      name: "accountnumber",
      label: "Account Number",
      placeholder: "Enter Account Number",
      fullWidth: false,
      type: "text",
    },
    {
      name: "accountType",
      label: "Account Type",
      placeholder: "Enter Account Type",
      fullWidth: false,
      type: "text",
    },
    {
      name: "swiftBicCode",
      label: "SWIFT / BIC Code",
      placeholder: "Enter Swift / BIC Code",
      fullWidth: false,
      type: "text",
    },
    {
      name: "currencyPreference",
      label: "Currency Preference",
      placeholder: "Enter Currency Preference",
      fullWidth: false,
      type: "currency",
    },
    {
      name: "wipayAccountNumber",
      label: "WiPay Account Number",
      placeholder: "Enter WiPay Account Number",
      fullWidth: false,
      type: "text",
    },
    {
      name: "wipayApiKey",
      label: "WiPay API Key",
      placeholder: "Enter WiPay API Key",
      fullWidth: false,
      type: "text",
    },
  ];

  const fuelStationDetailsFields = [
    {
      name: "stationName",
      label: "Station Name",
      placeholder: "Enter Fuel Station Name",
      fullWidth: false,
      type: "text",
    },
    {
      name: "stationId",
      label: "Station ID",
      placeholder: "Enter Fuel Station ID",
      fullWidth: false,
      type: "text",
    },
    {
      name: "fullPhysicalAddress",
      label: "Full Physical Address",
      placeholder: "Enter Fuel Station Address",
      fullWidth: true,
      type: "text",
    },
    {
      name: "fuelBrand",
      label: "Fuel Brand",
      placeholder: "Select Fuel Brand",
      fullWidth: false,
      type: "select",
      options: fuelBrandOptions,
    },
    {
      name: "latitude",
      label: "Latitude",
      placeholder: "Enter Latitude From the Address of Fuel Station",
      fullWidth: false,
      type: "text",
    },
    {
      name: "longitude",
      label: "Longitude",
      placeholder: "Enter Longitude From the Address of Fuel Station",
      fullWidth: false,
      type: "text",
    },
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!passedId) {
        message.error("No vendor selected.");
        return;
      }

      const formData = new FormData();
      formData.append("legal_name", values.legalEntityName);
      formData.append("brand_name", values.brandName);
      formData.append("vendor_type", values.vendorType || "");
      formData.append("currency", values.currency);
      formData.append("contact_phone", values.contactPhone);
      formData.append(
        "registration_contact_phone",
        values.registrationContactPhone || "",
      );
      formData.append("address", values.headquartersaddress);
      formData.append("primary_contact_person", values.primarycontactperson);
      formData.append("contact_email", values.businessemail);
      formData.append("station_name", values.stationName || "");
      formData.append("station_id", values.stationId || "");
      formData.append("station_address", values.fullPhysicalAddress || "");
      formData.append("fuel_brand_id", values.fuelBrand || "");
      if (values.latitude !== "" && values.latitude != null) {
        formData.append("latitude", values.latitude);
      }
      if (values.longitude !== "" && values.longitude != null) {
        formData.append("longitude", values.longitude);
      }

      formData.append(
        "bank_details",
        JSON.stringify({
          bank_name: values.bankName || "",
          bank_address: values.bankAddress || "",
          account_holder_name: values.accountHolderName || "",
          account_number: values.accountnumber || "",
          account_type: values.accountType || "",
          swift_code: values.swiftBicCode || "",
          settlement_currency: values.currencyPreference || "",
        }),
      );
      formData.append("wipay_account_number", values.wipayAccountNumber || "");
      formData.append("wipay_api_key", values.wipayApiKey || "");

      const selectedFuelTypeIds = fuelTypeCatalog
        .filter((fuel) => fuelTypes.includes(fuel.name))
        .map((fuel) => fuel.id);
      selectedFuelTypeIds.forEach((id) =>
        formData.append("fuel_types_offered_ids", id),
      );

      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }

      formData.append("operating_hours", JSON.stringify(operatingHours));

      const newDocuments = documents.filter((doc) => doc.file);
      newDocuments.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      if (newDocuments.length > 0) {
        formData.append(
          "document_categories",
          JSON.stringify(newDocuments.map((doc) => doc.category)),
        );
        formData.append(
          "document_expiry_dates",
          JSON.stringify(newDocuments.map((doc) => doc.expiryDate || null)),
        );
      }

      setSaving(true);
      const res = await updateVendorDetails(passedId, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          res?.data?.message || "Vendor details updated successfully!",
        );
        navigate("/vendor-detail", {
          state: { id: passedId },
          replace: true,
        });
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update vendor."));
      }
    } catch (err) {
      if (err?.errorFields) {
        notification.warning({
          message: "Missing Required Fields",
          description: "Please fill in all required fields before saving.",
          placement: "topRight",
        });
        return;
      }
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update vendor."),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-vendor">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
      <Form form={form} layout="vertical">
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  "?"
                )}
              </div>
            </div>

            <div className="profile-body"></div>
          </div>
        </div>
        <div>
          <div>
            <InputCard title="Fuel Vendor Registration" fields={vendorFields} />
          </div>
          <div>
            <InputCard title="Contact Information" fields={contactInfoFields} />
          </div>
          <div>
            <InputCard
              title="Banking & Settlement"
              fields={bankingDetailsFields}
            />
          </div>
          <div>
            <InputCard
              title="Fuel Station Information"
              fields={fuelStationDetailsFields.slice(0, 4)}
            />
            <div className="gps-banner">
              <InfoCircleOutlined className="gps-icon" />
              <span>
                <div className="heading-banner">
                  GPS Coordinate Requirement:
                </div>
                <div className="banner-sub-heading">
                  Every Geo-GPS coordinates are mandatory. These are used to
                  automatically validate fuel transactions ensuring accurate
                  location verification.
                </div>
              </span>
            </div>
            <InputCard title="" fields={fuelStationDetailsFields.slice(4)}>
              <LocationMap
                latitude={watchedLatitude ? parseFloat(watchedLatitude) : null}
                longitude={
                  watchedLongitude ? parseFloat(watchedLongitude) : null
                }
                label="Station Location"
                onLocationSelect={({ latitude, longitude }) =>
                  form.setFieldsValue({ latitude, longitude })
                }
              />
            </InputCard>
          </div>
          <div>
            <FuelType
              fuelList={
                fuelTypeCatalog.length
                  ? fuelTypeCatalog.map((fuel) => fuel.name)
                  : fuelTypes
              }
              selectedFuel={fuelTypes}
              onSelectFuel={setFuelTypes}
            />
          </div>
          <div>
            <GenericOperatingHours
              value={operatingHours}
              onChange={(data) => setOperatingHours(data)}
            />
          </div>
          <div>
            <GenericDocumentManagerTable
              initialDocuments={documents}
              setDocuments={setDocuments}
              documentTypes={documentCategories}
              onDeleteDocument={async (record) => {
                if (!record.id) return;

                const res = await deleteVendorDocument(record.id);
                if (Math.floor(res?.status / 100) !== 2) {
                  throw new Error(
                    getErrorMessage(res?.data, "Failed to delete document."),
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="btns-row">
          <Button children="Cancel" onClick={handleCancel} />
          <Button
            children="Save Changes"
            onClick={() => handleSubmit()}
            className="save-btn"
            loading={saving}
            disabled={saving}
          />
        </div>
      </Form>
    </div>
  );
};

export default EditVendorDetails;
